import React from 'react';
import styled from 'styled-components';
import xlsx from 'json-as-xlsx';
import {
	Table,
	Row,
	Col,
	ButtonGroup,
	Button,
	Spinner,
	Form,
	InputGroup,
	Card,
	Collapse,
	Badge,
} from 'react-bootstrap';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import FilterListIcon from '@material-ui/icons/FilterList';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import RefreshIcon from '@material-ui/icons/Refresh';
import SaveIcon from '@material-ui/icons/Save';

import {useWmsWeight} from '../hooks/useWmsWeight';
import {
	HAS_WEIGHT_OPTIONS,
	formatStoredWeight,
	buildExportRows,
	rowKey,
} from '../utils/helper';

const TableBodyWidth = styled.div`
  table tr th {
    white-space: nowrap;
    width: initial;
    scrollbar-width: none;
    text-overflow: ellipsis;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const StyledFormControl = styled(Form.Control)`
  border-radius: 0 !important;
  height: 100% !important;
`;

const InputGroupText = styled(InputGroup.Text)`
  border-radius: 0 !important;
`;

const WeightInput = styled(Form.Control)`
  max-width: 130px;
  ${(props) => props.$dirty && 'border-color: #ff9800; background-color: #fff8e1;'}
  ${(props) => props.$error && 'border-color: #d32f2f; background-color: #fdecea;'}
`;

const WmsWeightListing = () => {
	const {
		items,
		total,
		totalPages,
		page,
		limit,
		loading,
		error,
		inputPage,
		showFilter,
		filters,
		editedWeights,
		rowStatus,
		isSaving,
		saveSummary,
		dirtyRows,
		setInputPage,
		setShowFilter,
		setSaveSummary,
		loadProducts,
		handlePageChange,
		handleLimitChange,
		handleFilterChange,
		applyFilters,
		resetFilters,
		handleKeyPress,
		handleWeightChange,
		handleWeightBlur,
		handleSaveAll,
		handleDiscardAll,
	} = useWmsWeight();

	const dirtyCount = dirtyRows.length;

	const handleExport = () => {
		xlsx(
			[
				{
					sheet: 'WMS Weights',
					columns: [
						{label: 'SKU', value: 'product_code'},
						{label: 'Product', value: 'name'},
						{label: 'Variant', value: 'variant_name'},
						{label: 'Item ID', value: 'item_id'},
						{label: 'Price Type ID', value: 'price_type_id'},
						{label: 'Weight (g)', value: 'weight_grams'},
						{label: 'Stored Weight', value: 'stored_weight'},
						{label: 'Has Weight', value: 'has_weight'},
					],
					content: buildExportRows(items),
				},
			],
			{fileName: 'WMS_Product_Weights', extraLength: 3, writeMode: 'writeFile'}
		);
	};

	return (
		<div className="container-fluid">
			<Row className="align-items-center mb-2">
				<Col lg={6}>
					<h4 className="mb-0">
						Product Weights{' '}
						<small className="text-muted" style={{fontSize: '0.6em'}}>
							{total} variant{total === 1 ? '' : 's'}
						</small>
					</h4>
					<small className="text-muted">
						Weights are stored in grams. One row per product variant.
					</small>
				</Col>
				<Col lg={6}>
					<ButtonGroup className="pull-right float-right">
						<Button
							variant="primary"
							onClick={() => handlePageChange(1)}
							disabled={page === 1 || loading}
						>
							First
						</Button>
						<Button
							variant="primary"
							onClick={() => handlePageChange(page - 1)}
							disabled={page === 1 || loading}
						>
							<ArrowBackIosIcon style={{fontSize: '18px'}}/>
						</Button>
						<InputGroup style={{width: 'auto'}}>
							<StyledFormControl
								type="number"
								min={1}
								max={totalPages}
								value={inputPage || page}
								onChange={(e) => setInputPage(parseInt(e.target.value, 10) || '')}
								onBlur={() => {
									const newPage = parseInt(inputPage, 10);
									if (newPage && newPage >= 1 && newPage <= totalPages) {
										handlePageChange(newPage);
									}
									setInputPage('');
								}}
								onKeyPress={(e) => {
									if (e.key === 'Enter') e.target.blur();
								}}
								disabled={loading}
							/>
							<InputGroupText>of {totalPages}</InputGroupText>
						</InputGroup>
						<Button
							variant="primary"
							onClick={() => handlePageChange(page + 1)}
							disabled={page === totalPages || loading}
						>
							<NavigateNextIcon style={{fontSize: '25px'}}/>
						</Button>
						<Button
							variant="primary"
							onClick={() => handlePageChange(totalPages)}
							disabled={page === totalPages || loading}
						>
							Last
						</Button>
					</ButtonGroup>
				</Col>
			</Row>

			{/* Toolbar */}
			<Row className="align-items-center mb-2">
				<Col lg={7}>
					<Button
						variant="outline-secondary"
						onClick={() => setShowFilter(!showFilter)}
						className="mr-2"
					>
						{showFilter ? <FilterNoneIcon style={{fontSize: 18}}/> : <FilterListIcon style={{fontSize: 18}}/>}
						&nbsp;{showFilter ? 'Hide filters' : 'Show filters'}
					</Button>
					<Button variant="outline-secondary" onClick={loadProducts} disabled={loading} className="mr-2">
						<RefreshIcon style={{fontSize: 18}}/>&nbsp;Refresh
					</Button>
					<Button variant="success" onClick={handleExport} disabled={!items.length}>
						Export
					</Button>
				</Col>
				<Col lg={5} className="text-right">
					{dirtyCount > 0 && (
						<>
							<Badge variant="warning" className="mr-2" style={{fontSize: '0.85rem'}}>
								{dirtyCount} unsaved
							</Badge>
							<Button variant="link" onClick={handleDiscardAll} disabled={isSaving} className="mr-2">
								Discard
							</Button>
						</>
					)}
					<Button variant="primary" onClick={handleSaveAll} disabled={!dirtyCount || isSaving}>
						{isSaving ? (
							<Spinner animation="border" size="sm"/>
						) : (
							<SaveIcon style={{fontSize: 18}}/>
						)}
						&nbsp;Save {dirtyCount > 0 ? `${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'changes'}
					</Button>
				</Col>
			</Row>

			{/* Filters */}
			<Collapse in={showFilter}>
				<div>
					<Card className="mb-2">
						<Card.Body>
							<Row>
								<Col md={4}>
									<Form.Group>
										<Form.Label>Search (SKU or name)</Form.Label>
										<Form.Control
											type="text"
											value={filters.search}
											placeholder="e.g. 9340746000829 or Edamame"
											onChange={(e) => handleFilterChange('search', e.target.value)}
											onKeyPress={handleKeyPress}
										/>
									</Form.Group>
								</Col>
								<Col md={3}>
									<Form.Group>
										<Form.Label>Weight status</Form.Label>
										<Form.Control
											as="select"
											value={filters.has_weight}
											onChange={(e) => handleFilterChange('has_weight', e.target.value, true)}
										>
											{HAS_WEIGHT_OPTIONS.map((opt) => (
												<option key={opt.label} value={opt.value}>{opt.label}</option>
											))}
										</Form.Control>
									</Form.Group>
								</Col>
								<Col md={2}>
									<Form.Group>
										<Form.Label>Supplier ID</Form.Label>
										<Form.Control
											type="number"
											value={filters.supplier_id}
											onChange={(e) => handleFilterChange('supplier_id', e.target.value)}
											onKeyPress={handleKeyPress}
										/>
									</Form.Group>
								</Col>
								<Col md={2}>
									<Form.Group>
										<Form.Label>Store ID</Form.Label>
										<Form.Control
											type="number"
											value={filters.store_id}
											onChange={(e) => handleFilterChange('store_id', e.target.value)}
											onKeyPress={handleKeyPress}
										/>
									</Form.Group>
								</Col>
								<Col md={1}>
									<Form.Group>
										<Form.Label>Rows</Form.Label>
										<Form.Control
											as="select"
											value={limit}
											onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
										>
											{[25, 50, 100, 200, 500].map((n) => (
												<option key={n} value={n}>{n}</option>
											))}
										</Form.Control>
									</Form.Group>
								</Col>
							</Row>
							<Row className="align-items-center">
								<Col md={4}>
									<Form.Check
										type="checkbox"
										id="wms-include-deleted"
										label="Include deleted products"
										checked={filters.include_deleted}
										onChange={(e) => handleFilterChange('include_deleted', e.target.checked, true)}
									/>
								</Col>
								<Col md={8} className="text-right">
									<Button variant="outline-secondary" onClick={resetFilters} className="mr-2">
										Reset
									</Button>
									<Button variant="primary" onClick={applyFilters}>
										Apply filters
									</Button>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</div>
			</Collapse>

			{error && <div className="alert alert-danger">{error}</div>}

			{saveSummary && (
				<div
					className={`alert ${saveSummary.failed ? 'alert-warning' : 'alert-success'} d-flex justify-content-between align-items-center`}
				>
					<span>{saveSummary.message}</span>
					<Button variant="link" size="sm" onClick={() => setSaveSummary(null)}>Dismiss</Button>
				</div>
			)}

			<TableBodyWidth
				className="table-responsive"
				style={{height: 'calc(100vh - 340px)', marginTop: '5px'}}
			>
				{loading ? (
					<div className="d-flex justify-content-center align-items-center h-100">
						<Spinner animation="border" role="status">
							<span className="sr-only">Loading...</span>
						</Spinner>
					</div>
				) : (
					<Table striped bordered hover style={{width: '100%'}}>
						<thead>
							<tr>
								<th style={{width: '150px'}}>SKU</th>
								<th>Product</th>
								<th style={{width: '160px'}}>Variant</th>
								<th style={{width: '90px'}}>Item ID</th>
								<th style={{width: '110px'}}>Price Type</th>
								<th style={{width: '130px'}}>Stored</th>
								<th style={{width: '160px'}}>Weight (g)</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && (
								<tr>
									<td colSpan={7} className="text-center text-muted py-4">
										No products found for the current filters.
									</td>
								</tr>
							)}
							{items.map((item) => {
								const key = rowKey(item);
								const edited = editedWeights[key];
								const value = edited !== undefined ? edited : (item.weight_grams ?? '');
								const isDirty = dirtyRows.some((r) => r.price_type_id === item.price_type_id);
								const status = rowStatus[key];
								const rowError = status && status !== 'saving' ? status : null;

								return (
									<tr key={key}>
										<td>{item.product_code || '-'}</td>
										<td>
											{item.name || '-'}
											{item.name_km && item.name_km !== item.name && (
												<div className="text-muted" style={{fontSize: 12}}>{item.name_km}</div>
											)}
										</td>
										<td>{item.variant_name || '-'}</td>
										<td>{item.item_id}</td>
										<td>{item.price_type_id}</td>
										<td>
											{formatStoredWeight(item)}
											{!item.has_weight && (
												<Badge variant="secondary" className="ml-1">none</Badge>
											)}
										</td>
										<td>
											<WeightInput
												type="number"
												min="0"
												step="any"
												$dirty={isDirty && !rowError ? 1 : 0}
												$error={rowError ? 1 : 0}
												title={rowError || undefined}
												value={value}
												placeholder="grams"
												disabled={rowStatus[key] === 'saving'}
												onChange={(e) => handleWeightChange(item.price_type_id, e.target.value)}
												onBlur={() => handleWeightBlur(item)}
												onKeyPress={(e) => {
													if (e.key === 'Enter') e.target.blur();
												}}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				)}
			</TableBodyWidth>
		</div>
	);
};

export default WmsWeightListing;
